import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {
  AuthenticateModalContent,
  CreditsPillContent,
  CreditsPillTheme,
  EditableComponentsMap,
  PromptOption,
  SelectedPromptOptionValues,
  UpsellDialogContent,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPill} from '@Components/ai-resdesign-prompt-box/components/credits-pill';
import Divider from '@Components/divider';
import styles from './input-box-footer.module.scss';
import {SubmitButton} from '@Components/ai-resdesign-prompt-box/components/submit-button';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {AIRedesignContextualMenu} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-contextual-menu';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import type {AsyncVoidFunction} from "@Utils/general.util";

interface InputBoxFooterProps {
  showControls: boolean;
  isPromptBoxSelected: boolean;
  isLoading: boolean;
  isSubmitButtonActive: boolean;
  onSubmission: AsyncVoidFunction;
  creditsPillTheme: CreditsPillTheme;
  promptOptionsToUse: PromptOption[];
  selectedPromptOptionValues: SelectedPromptOptionValues;
  onSelectedPromptOptionValuesChange: (promptOptionValues: SelectedPromptOptionValues) => void;
  editableComponentsMap: EditableComponentsMap;
  onEditableComponentsMapChange: (editableComponentsMap: EditableComponentsMap) => void;
  upsellDialog?: UpsellDialogContent;
  creditsPillContent?: CreditsPillContent;
  authenticateModalContent?: AuthenticateModalContent;
}

export function InputBoxFooter({creditsPillContent, ...props}: InputBoxFooterProps): ReactElement {
  const footerRef = useRef<HTMLDivElement>(null);
  const contextualMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [contextualMenuWidth, setContextualMenuWidth] = useState<number>(0);
  const [showContextualMenuDropdown, setShowContextualMenuDropdown] = useState(false);
  const showUpsellDialog = props.upsellDialog ? props.upsellDialog.showDialog : false;
  const showAuthenticateModal = props.authenticateModalContent ? props.authenticateModalContent.showModal : false;
  const upsellDialog = props.upsellDialog ? props.upsellDialog.dialogName : undefined;
  const CONTEXTUAL_MENU_POPUP_OFFSET = 16;

  useEffect(() => {
    if (footerRef.current) {
      const width = footerRef.current.offsetWidth;
      setContextualMenuWidth(width);
    }

    document.addEventListener('touchstart', closeContextualMenuDropdownListener);
    document.addEventListener('mousedown', closeContextualMenuDropdownListener);

    return (): void => {
      document.removeEventListener('touchstart', closeContextualMenuDropdownListener);
      document.removeEventListener('mousedown', closeContextualMenuDropdownListener);
    };
  }, []);

  const closeContextualMenuDropdownListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!contextualMenuDropdownRef.current?.contains(target)) {
      setShowContextualMenuDropdown(false);
    }
  };

  const getContextualMenu = (): ReactElement => {
    return (
      <AIRedesignContextualMenu
        promptOptionsToUse={props.promptOptionsToUse}
        selectedPromptOptionValues={props.selectedPromptOptionValues}
        onSelectedPromptOptionValuesChange={props.onSelectedPromptOptionValuesChange}
        contextualMenuWidth={contextualMenuWidth}
        editableComponentsMap={props.editableComponentsMap}
        onEditableComponentsMapChange={props.onEditableComponentsMapChange}
      />
    );
  };

  const getContextualMenuDropdown = (): ReactElement => {
    return (
      <div ref={contextualMenuDropdownRef}>
        <Dropdown
          open={showContextualMenuDropdown}
          onOpen={() => {
            setShowContextualMenuDropdown(true);
          }}
          onClose={() => {
            setShowContextualMenuDropdown(false);
          }}
          doesCloseOnOuterClick
          doCloseOnInnerClick={false}
          selector={<Icon icon={'icon-adjustments'} size={IconSize.SIZE_ICON_16} className={`spacing-p-2 ${styles.contextualMenuDropdownSelector}`} />}
          selectorClassNameOnOpen={styles.contextualMenuDropdownSelectorOpen}
          popup={getContextualMenu()}
          popupClassName={styles.contextualMenuPopup}
          alignment={ALIGNMENT_TYPE.BOTTOM_START}
          shouldShift={false}
          popupOffset={CONTEXTUAL_MENU_POPUP_OFFSET}
          className={props.isPromptBoxSelected ? '' : styles.hidden}
          showModalForSmallScreen
          modalTitle={window.i18next.t('pmwjs_preferences')}
          modalClassName={styles.contextualMenuModal}
        />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement | undefined => {
    if (showAuthenticateModal || showUpsellDialog) {
      return <Icon icon={`icon-crown`} className={`${props.showControls ? '' : styles.hidden} ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.PRIMARY_DARK} />;
    }

    if (creditsPillContent) {
      return <CreditsPill showPill={props.showControls} theme={props.creditsPillTheme} content={creditsPillContent} />;
    }

    return undefined;
  };

  const getDivider = (): ReactElement | undefined => {
    if (showAuthenticateModal || showUpsellDialog || creditsPillContent) {
      return <Divider width={1} height={20} additionalClasses={props.showControls ? '' : styles.hidden} />;
    }

    return undefined;
  };

  const getCreditsPillAndSubmitButton = (): ReactElement => {
    return (
        <div className={'flex-row-justify-between flex-items-center gap-2'}>
          {getCreditsPill()}
          {getDivider()}
          <SubmitButton
              isLoading={props.isLoading}
              isActive={props.isSubmitButtonActive}
              onSubmission={props.onSubmission}
              showUpsellDialog={showUpsellDialog}
              upsellDialog={upsellDialog}
              authenticateModalContent={props.authenticateModalContent}
          />
        </div>
    );
  };

  const getFooter = (): ReactElement => {
    return (
      <div ref={footerRef} className={`flex-row-justify-between flex-align-items-center spacing-p-r-2 spacing-p-l-2 spacing-p-b-2 spacing-p-t-1`}>
        {getContextualMenuDropdown()}
        {getCreditsPillAndSubmitButton()}
      </div>
    );
  };

  return getFooter();
}
